import { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Users, Linkedin, Mail, Phone, Building2, Search, GitBranch,
  LayoutGrid, ChevronDown, ChevronRight, User
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Helper function to get image URL
const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${API_URL}${url}`;
};

// Hierarchy Node Component - defined outside to avoid re-creation
function HierarchyNodeItem({ member, level, expandedNodes, toggleNode, setSelectedMember }) {
  const hasChildren = member.children && member.children.length > 0;
  const isExpanded = expandedNodes[member.id];
  
  return (
    <div className="relative">
      {level > 0 && (
        <div className="absolute left-0 top-0 h-full">
          <div className="absolute left-6 top-0 w-px h-1/2 bg-border" />
          <div className="absolute left-6 top-1/2 w-4 h-px bg-border" />
        </div>
      )}
      
      <div className={`flex items-start gap-2 ${level > 0 ? 'ml-10' : ''}`}>
        <div 
          className="flex items-center gap-3 p-3 bg-card rounded-lg border hover:shadow-md transition-shadow cursor-pointer flex-1"
          onClick={() => setSelectedMember(member)}>
          {hasChildren && (
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-6 w-6 shrink-0"
              onClick={(e) => { e.stopPropagation(); toggleNode(member.id); }}
            >
              {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </Button>
          )}
          
          <Avatar className="h-12 w-12 shrink-0">
            <AvatarImage src={getImageUrl(member.profile_image_url)} alt={member.name} />
            <AvatarFallback className="bg-indigo-100 text-indigo-600">
              {member.name?.charAt(0) || 'U'}
            </AvatarFallback>
          </Avatar>
          
          <div className="flex-1 min-w-0">
            <p className="font-medium text-foreground">{member.name}</p>
            <p className="text-sm text-indigo-600">{member.designation}</p>
            {member.department && (
              <p className="text-xs text-muted-foreground">{member.department}</p>
            )}
          </div>
          
          <div className="flex items-center gap-2 shrink-0">
            {member.email && (
              <a 
                href={`mailto:${member.email}`} 
                className="text-muted-foreground hover:text-blue-500"
                onClick={(e) => e.stopPropagation()}
              >
                <Mail className="h-4 w-4" />
              </a>
            )}
            {member.linkedin_url && (
              <a 
                href={member.linkedin_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-blue-600"
                onClick={(e) => e.stopPropagation()}
              >
                <Linkedin className="h-4 w-4" />
              </a>
            )}
          </div>
        </div>
      </div>
      
      {hasChildren && isExpanded && (
        <div className="mt-2 space-y-2">
          {member.children.map(child => (
            <HierarchyNodeItem 
              key={child.id} 
              member={child} 
              level={level + 1}
              expandedNodes={expandedNodes}
              toggleNode={toggleNode}
              setSelectedMember={setSelectedMember}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function UserTeam() {
  const { user } = useOutletContext();
  const [members, setMembers] = useState([]);
  const [hierarchy, setHierarchy] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('gallery');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMember, setSelectedMember] = useState(null);
  const [expandedNodes, setExpandedNodes] = useState({});

  useEffect(() => {
    loadTeam();
  }, []);

  const loadTeam = async () => {
    try {
      const [membersRes, hierarchyRes] = await Promise.all([
        fetch(`${API_URL}/api/team/members`),
        fetch(`${API_URL}/api/team/hierarchy`)
      ]);
      
      if (membersRes.ok) {
        const data = await membersRes.json();
        setMembers(data.members || []);
      }
      
      if (hierarchyRes.ok) {
        const data = await hierarchyRes.json();
        setHierarchy(data.hierarchy || []);
        // Expand all nodes by default
        const expanded = {};
        const expandAll = (nodes) => {
          nodes.forEach(node => {
            expanded[node.id] = true;
            if (node.children?.length) expandAll(node.children);
          });
        };
        expandAll(data.hierarchy || []);
        setExpandedNodes(expanded);
      }
    } catch (error) {
      console.error('Failed to load team:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredMembers = members.filter(member => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      member.name?.toLowerCase().includes(query) ||
      member.designation?.toLowerCase().includes(query) ||
      member.department?.toLowerCase().includes(query)
    );
  });

  const toggleNode = useCallback((nodeId) => {
    setExpandedNodes(prev => ({
      ...prev,
      [nodeId]: !prev[nodeId]
    }));
  }, []);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-32 rounded-lg" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-64" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg p-6 text-white">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-card/20 rounded-lg">
            <Users className="h-8 w-8" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Know the Team</h1>
            <p className="text-indigo-100 mt-1">
              Meet the Quality Department team
            </p>
          </div>
        </div>
      </div>

      {/* Search & View Toggle */}
      <div className="flex items-center justify-between gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search team members..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="gallery" className="flex items-center gap-2">
              <LayoutGrid className="h-4 w-4" />
              Gallery
            </TabsTrigger>
            <TabsTrigger value="hierarchy" className="flex items-center gap-2">
              <GitBranch className="h-4 w-4" />
              Hierarchy
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Gallery View */}
      {activeTab === 'gallery' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredMembers.length === 0 ? (
            <Card className="col-span-full">
              <CardContent className="text-center py-12">
                <Users className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
                <p className="text-muted-foreground">
                  {members.length === 0 ? 'No team members yet' : 'No matching results'}
                </p>
              </CardContent>
            </Card>
          ) : (
            filteredMembers.map((member) => (
              <Card 
                key={member.id} 
                className="overflow-hidden cursor-pointer hover:shadow-lg transition-shadow"
                onClick={() => setSelectedMember(member)}
              >
                <div className="h-28 bg-gradient-to-br from-indigo-400 to-purple-500 relative">
                  <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2">
                    <Avatar className="h-16 w-16 border-4 border-white shadow-lg">
                      <AvatarImage src={getImageUrl(member.profile_image_url)} alt={member.name} />
                      <AvatarFallback className="bg-indigo-100 text-indigo-600 text-lg">
                        {member.name?.charAt(0) || 'U'}
                      </AvatarFallback>
                    </Avatar>
                  </div>
                </div>
                
                <CardContent className="pt-12 pb-4 text-center">
                  <h3 className="font-semibold">{member.name}</h3>
                  <p className="text-sm text-indigo-600">{member.designation}</p>
                  
                  {member.department && (
                    <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground mt-1">
                      <Building2 className="h-3 w-3" />
                      {member.department}
                    </div>
                  )}
                  
                  {member.reports_to_name && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Reports to: {member.reports_to_name}
                    </p>
                  )}
                  
                  <div className="flex items-center justify-center gap-3 mt-3">
                    {member.email && (
                      <a 
                        href={`mailto:${member.email}`} 
                        className="text-muted-foreground hover:text-blue-500"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Mail className="h-4 w-4" />
                      </a>
                    )}
                    {member.phone && (
                      <a 
                        href={`tel:${member.phone}`} 
                        className="text-muted-foreground hover:text-green-500"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Phone className="h-4 w-4" />
                      </a>
                    )}
                    {member.linkedin_url && (
                      <a 
                        href={member.linkedin_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-muted-foreground hover:text-blue-600"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Linkedin className="h-4 w-4" />
                      </a>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      {/* Hierarchy View */}
      {activeTab === 'hierarchy' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <GitBranch className="h-5 w-5 text-indigo-500" />
              Organization Chart
            </CardTitle>
            <CardDescription>
              Click on a team member to view their details
            </CardDescription>
          </CardHeader>
          <CardContent>
            {hierarchy.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <GitBranch className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No hierarchy data available</p>
              </div>
            ) : (
              <div className="space-y-3">
                {hierarchy.map(member => (
                  <HierarchyNodeItem 
                    key={member.id} 
                    member={member} 
                    level={0}
                    expandedNodes={expandedNodes}
                    toggleNode={toggleNode}
                    setSelectedMember={setSelectedMember}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Member Detail Dialog */}
      <Dialog open={!!selectedMember} onOpenChange={() => setSelectedMember(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="sr-only">Team Member Details</DialogTitle>
          </DialogHeader>
          
          {selectedMember && (
            <div className="space-y-6">
              {/* Profile Header */}
              <div className="text-center">
                <Avatar className="h-24 w-24 mx-auto mb-4 border-4 border-indigo-100">
                  <AvatarImage src={getImageUrl(selectedMember.profile_image_url)} alt={selectedMember.name} />
                  <AvatarFallback className="bg-indigo-100 text-indigo-600 text-2xl">
                    {selectedMember.name?.charAt(0) || 'U'}
                  </AvatarFallback>
                </Avatar>
                <h2 className="text-xl font-bold text-foreground">{selectedMember.name}</h2>
                <p className="text-indigo-600 font-medium">{selectedMember.designation}</p>
                {selectedMember.department && (
                  <Badge variant="outline" className="mt-2">
                    <Building2 className="h-3 w-3 mr-1" />
                    {selectedMember.department}
                  </Badge>
                )}
              </div>

              {/* Overview */}
              {selectedMember.overview && (
                <div className="bg-muted/50 rounded-lg p-4">
                  <p className="text-sm text-muted-foreground">{selectedMember.overview}</p>
                </div>
              )}

              {/* Reports To */}
              {selectedMember.reports_to_name && (
                <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                  <User className="h-4 w-4" />
                  Reports to: <span className="font-medium text-foreground">{selectedMember.reports_to_name}</span>
                </div>
              )}

              {/* Contact Actions */}
              <div className="grid grid-cols-3 gap-3">
                {selectedMember.email && (
                  <a 
                    href={`mailto:${selectedMember.email}`}
                    className="flex flex-col items-center gap-2 p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                  >
                    <Mail className="h-5 w-5 text-blue-500" />
                    <span className="text-xs text-blue-700">Email</span>
                  </a>
                )}
                {selectedMember.phone && (
                  <a 
                    href={`tel:${selectedMember.phone}`}
                    className="flex flex-col items-center gap-2 p-4 bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
                  >
                    <Phone className="h-5 w-5 text-green-500" />
                    <span className="text-xs text-green-700">Call</span>
                  </a>
                )}
                {selectedMember.linkedin_url && (
                  <a 
                    href={selectedMember.linkedin_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex flex-col items-center gap-2 p-4 bg-indigo-50 rounded-lg hover:bg-indigo-100 transition-colors"
                  >
                    <Linkedin className="h-5 w-5 text-indigo-500" />
                    <span className="text-xs text-indigo-700">LinkedIn</span>
                  </a>
                )}
              </div>

              {/* Contact Info */}
              <div className="text-sm text-muted-foreground text-center space-y-1">
                {selectedMember.email && <p>{selectedMember.email}</p>}
                {selectedMember.phone && <p>{selectedMember.phone}</p>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
